import { useState, useEffect } from 'react'
import Section from './_Section'
import { createMarkup } from '../../helpers/markup'
import { detectShadeClash, transformPaddingToTailwind } from '../../helpers/style'
import ButtonEl from '../helpers/ctas/Button'
import { CheckIcon } from '@heroicons/react/24/solid'
import PercentValue from '../helpers/PercentValue'
import themeConfig from '../../../theme.config'

function RatesCardList(props) {
  const [highlightColor, setHighlightColor] = useState('secondary')
  const componentPadding = transformPaddingToTailwind(props.componentPadding)
  const [textAlignment, setTextAlignment] = useState({
    flex: 'center',
    align: 'center',
  })
  const [cardCount, setCardCount] = useState(0)

  const classesFor = idx => {
    const c = ['lg:col-span-6', 'xl:col-span-4']

    if (cardCount % 2 === 1 && idx === cardCount -1) c.push('lg-center')

    const r = cardCount % 3
    if (r === 1 && idx === cardCount - 1) c.push('xl-center-1')
    if (r === 2 && idx === cardCount - 2) c.push('xl-pair-a')
    if (r === 2 && idx === cardCount - 1) c.push('xl-pair-b')

    return c.join(' ')
  }

  useEffect(() => {
    setTextAlignment((prevState) => {
      const newState = { ...prevState }
      const alignment = props.contentAlignment?.toLowerCase() ?? 'center'
      if (newState.align !== props.contentAlignment?.toLowerCase()) {
        newState.flex = alignment === 'center' ? 'center' : 'start'
        newState.align = alignment === 'center' ? 'center' : 'left'
      }
      return newState
    })
    setCardCount(props.products?.length || 0)
  }, [])

  // this component explicitly uses the secondary shade so if it clashes with the selected background colour we need to change it.
  useEffect(() => {
    setHighlightColor((prevState) => {
      if (detectShadeClash(props.backgroundColor, prevState)) {
        return 'primary'
      }
      return prevState
    })
  }, [])

  return (
    <Section type="ratesCardList" outerClass={componentPadding} innerClass="px-8" {...props}>
      {props.products?.length ? (
        <div className="grid max-lg:divide-y-2 lg:divide-x-2 divide-secondary divide-dotted grid-cols-1 lg:grid-cols-12 grid-flow-row-dense grid-flow-col grid-rows-[repeat(10,_auto)] mt-4 lg:mb-16 gap-y-0 gap-x-0 grid-centered">
          {props.products.map((item, i) => {
            const col = i + 1
            return (
              <div className={`grid grid-rows-subgrid row-span-6 mb-8 lg:mb-16 px-4 max-lg:pt-8 xl:[&:nth-child(3n-2)]:!border-l-0 lg:max-xl:[&:nth-child(2n-1)]:!border-l-0 ${classesFor(i)}`}>
                <div className={`font-bold text-lg text-center`}>{item.title}</div>

                <div data-rel="Description">
                  {item.description && (
                    <div
                      className={`text-sm mt-2 ${themeConfig.settings.ratesCard.highlightDescription ? `text-${highlightColor}-500` : ''}`}
                      data-rte="true"
                      dangerouslySetInnerHTML={createMarkup(item.description)}
                    ></div>
                  )}
                </div>

                {item.informations?.length && item.informations[0]?.title?.length ? (
                  <div className={`mt-6 flex flex-col justify-start gap-4 text-center`}>
                    <div
      className={`text-2xl text-secondary-500 leading-7 ${themeConfig.settings.rates?.useHeroFont ? 'font-highlight' : ''}`}
      >
                        {item.informations[0].title}
                      </div>
                      {item.informations[0].content && (
                        <div data-rte="true" dangerouslySetInnerHTML={createMarkup(item.informations[0].content)}>
                        </div>
                      )}
                  </div>
                ) : ('')}

                {!item.informations?.length && item.variants?.length && (item.variants[0]?.interestRate || item.variants[0]?.interestRateDisclaimer) ? (
                  <div className={`mt-6 flex justify-around max-sm:flex-col max-sm:gap-4 text-center`}>
                      <div
                        className={`${themeConfig.settings.ratesCard.highlightRate ? `text-${highlightColor}-500` : ''}`}
                      >
                        <PercentValue
                          disclaimer={item.variants[0].interestRateDisclaimer}
                          rate={item.variants[0].interestRate.length > 0 ? item.variants[0].interestRate : 'n/a'}
                          rateType="Interest"
                        />
                      </div>
                    {item.variants[0]?.comparisonRate && (
                      <div>
                        <PercentValue
                          disclaimer={item.variants[0].comparisonRateDisclaimer}
                          rate={item.variants[0].comparisonRate}
                          rateType="Comparison"
                        />
                      </div>
                    )}
                  </div>
                ) : ('')}
                
                {!item.informations?.length && !item.variants?.length ? (
                  // deals with a product with no variants (and therefore no rates) being set alongside products with rates, causing a vertical layout issue.
                  <div>&nbsp;</div>
                ): ('')}

                <div data-rel="Unique selling points">
                  {item.uniqueSellingPoints?.length ? (
                    <ul className={`mt-10`}>
                      {item.uniqueSellingPoints.map((item, i) => (
                        <li key={i} className="mb-4 flex">
                          <CheckIcon role="none" className={`h-6 w-6 mr-4 text-${highlightColor}-500 flex-shrink-0`} />
                          {item}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>

                <div data-rel="Disclaimers">
                  {themeConfig.settings.ratesCard.showDisclaimersFromCMS && (
                    <div className="mt-4 border-t-2 py-4 text-sm">
                      {item.variants[0]?.interestRateDisclaimer?.length > 0 && (
                        <div>
                          <sup>{themeConfig.settings.ratesCard.disclaimerIcons[0]}</sup>{' '}
                          {item.variants[0]?.interestRateDisclaimer}
                        </div>
                      )}
                      {item.variants[0]?.comparisonRateDisclaimer?.length > 0 && (
                        <div>
                          <sup>{themeConfig.settings.ratesCard.disclaimerIcons[1]}</sup>{' '}
                          {item.variants[0]?.comparisonRateDisclaimer}
                        </div>
                      )}
                    </div>
                  )}
                  {item.productDisclaimer?.length > 0 && (
                    <div
                      className="text-sm mb-6"
                      data-rte="true"
                      dangerouslySetInnerHTML={createMarkup(item.productDisclaimer)}
                    ></div>
                  )}
                </div>

                {item.ctaLink?.length ? (
                  <ul className={``}>
                    {item.ctaLink
                      .filter((item) => item.link)
                      .map((item, j) => (
                        <li key={j} className="mt-6">
                          <ButtonEl
                            link={item.link}
                            theme={item.linkType.toLowerCase()}
                            className="!justify-center !flex"
                          />
                        </li>
                      ))}
                  </ul>
                ) : null}
                {/* // </li> */}
              </div>
            )
          })}
        </div>
      ) : null}{props.ctaLink.length != 0 && (
        <div className={`flex justify-${textAlignment.flex}  space-x-6 mt-10`}>
          {props.ctaLink.map((ctaLinkItem, i) => (
            <ButtonEl
              key={i}
              link={ctaLinkItem.link}
              icon={ctaLinkItem.linkIconClass}
              theme={ctaLinkItem.linkType.toLowerCase()}
            />
          ))}
        </div>
      )}
    </Section>
  )
}

export default RatesCardList
